import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Share2, Trash2, Gift, UserPlus, MapPin, Eye } from 'lucide-react';
import InvitationBuilder from '@/components/InvitationBuilder';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [collaboratorEmail, setCollaboratorEmail] = useState('');
  const [formData, setFormData] = useState({
    product_name: '',
    product_url: '',
    product_image_url: '',
    price: ''
  });
  const [fetchingImage, setFetchingImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [priceFilter, setPriceFilter] = useState('all');
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressData, setAddressData] = useState({
    delivery_address: '',
    delivery_phone: ''
  });
  const [hasInvitation, setHasInvitation] = useState(false);
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);
  const [rsvps, setRsvps] = useState([]);

  useEffect(() => {
    checkInvitation();
  }, [eventId]);

  const checkInvitation = async () => {
    try {
      await axios.get(`${API}/events/${eventId}/invitation`, { withCredentials: true });
      setHasInvitation(true);
    } catch (error) {
      setHasInvitation(false);
    }
  };

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      const [eventRes, itemsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`, { withCredentials: true }),
        axios.get(`${API}/events/${eventId}/items`, { withCredentials: true })
      ]);
      setEvent(eventRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      console.error('Error fetching event data:', error);
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is creator or collaborator
  const isCreatorOrCollaborator = () => {
    if (!event || !user) return false;
    return event.creator_id === user.id || (event.collaborators || []).includes(user.id);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, product_image_url: response.data.url }));
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFetchProduct = async () => {
    if (!formData.product_url) return;
    
    setFetchingImage(true);
    try {
      const response = await axios.post(`${API}/fetch-product`, { url: formData.product_url });
      const newData = {
        ...formData,
        product_name: response.data.product_name || formData.product_name,
        product_image_url: response.data.product_image_url || formData.product_image_url,
        price: response.data.price || formData.price
      };
      setFormData(newData);
      
      // Check what was fetched
      const fetched = [];
      if (response.data.product_name) fetched.push('name');
      if (response.data.product_image_url) fetched.push('image');
      if (response.data.price) fetched.push('price');
      
      if (fetched.length === 0) {
        toast.warning('Could not auto-fill details. Please enter manually.');
      } else {
        toast.success(`Fetched: ${fetched.join(', ')}! Review and edit if needed.`);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to fetch details';
      toast.error(errorMsg + ' Please enter manually.');
    } finally {
      setFetchingImage(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    // Validate required fields (image is optional)
    if (!formData.product_name) {
      toast.error('Please enter product name');
      return;
    }
    if (!formData.price) {
      toast.error('Please enter price');
      return;
    }
    if (!formData.product_url) {
      toast.error('Please enter product URL');
      return;
    }

    try {
      // Use placeholder if no image provided
      const imageUrl = formData.product_image_url || 
        'https://via.placeholder.com/300x300/e5e7eb/6b7280?text=No+Image';
      
      await axios.post(`${API}/events/${eventId}/items`, {
        product_name: formData.product_name,
        product_image_url: imageUrl,
        product_url: formData.product_url,
        price: parseFloat(formData.price)
      }, { withCredentials: true });
      toast.success('Item added to wishlist!');
      setDialogOpen(false);
      setFormData({ product_name: '', product_url: '', product_image_url: '', price: '' });
      fetchEventData();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await axios.delete(`${API}/events/${eventId}/items/${itemId}`, { withCredentials: true });
      toast.success('Item deleted successfully');
      fetchEventData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleShareInvitation = async () => {
    // Fetch invitation details if not already loaded
    let invitationData = null;
    try {
      const response = await axios.get(`${API}/events/${eventId}/invitation`);
      invitationData = response.data;
    } catch (error) {
      console.error('Error fetching invitation:', error);
    }

    const inviteUrl = `${window.location.origin}/i/${eventId}`;
    
    let message = `🎉 You're invited to ${event.event_name}! 🎉\n\n`;
    
    if (invitationData) {
      message += `Join us for ${invitationData.person_names}'s ${invitationData.event_type}!\n\n`;
      message += `📅 Date: ${invitationData.date_from}\n`;
      message += `⏰ Time: ${invitationData.time_from}\n`;
      message += `📍 Venue: ${invitationData.venue}\n\n`;
    }
    
    message += `View the full invitation: ${inviteUrl}`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShareWishlist = () => {
    const wishlistUrl = `${window.location.origin}/e/${eventId}`;
    const message = `Check out my wishlist for ${event.event_name}! ${wishlistUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/e/${eventId}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };

  const handleShareWithCollaborator = async (e) => {
    e.preventDefault();
    
    if (!collaboratorEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      await axios.post(`${API}/events/${eventId}/share`, {
        collaborator_email: collaboratorEmail
      }, { withCredentials: true });
      toast.success('Event shared successfully! They can now add items to the wishlist.');
      setShareDialogOpen(false);
      setCollaboratorEmail('');
    } catch (error) {
      console.error('Error sharing event:', error);
      toast.error(error.response?.data?.detail || 'Failed to share event');
    }
  };

  const openAddressDialog = () => {
    setAddressData({
      delivery_address: event?.delivery_address || '',
      delivery_phone: event?.delivery_phone || ''
    });
    setAddressDialogOpen(true);
  };

  const fetchRSVPs = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/rsvps`, { withCredentials: true });
      setRsvps(response.data);
      setRsvpDialogOpen(true);
    } catch (error) {
      console.error('Error fetching RSVPs:', error);
      toast.error('Failed to load RSVP list');
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/events/${eventId}`, addressData, { withCredentials: true });
      toast.success('Delivery address updated successfully!');
      setAddressDialogOpen(false);
      fetchEventData();
    } catch (error) {
      console.error('Error updating address:', error);
      toast.error('Failed to update address');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_giftify-9/artifacts/t6482xxc_Screenshot%202025-11-16%20012420.png" 
              alt="My Wishlist Logo" 
              className="h-12 w-auto cursor-pointer"
              onClick={() => navigate('/home')}
              data-testid="event-logo"
            />
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="gap-2"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2" data-testid="event-title">{event?.event_name}</h1>
              <p className="text-gray-600" data-testid="event-date-time">{event?.event_date} at {event?.event_time}</p>
              {event?.creator_name && (
                <p className="text-sm text-gray-500 mt-1" data-testid="event-creator">Created by {event.creator_name}</p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="gap-2"
                    data-testid="share-collaborator-button"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Collaborator
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" data-testid="share-collaborator-dialog">
                  <DialogHeader>
                    <DialogTitle>Share Event with Collaborator</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleShareWithCollaborator} className="space-y-4">
                    <div>
                      <Label htmlFor="collaborator_email">Collaborator Email</Label>
                      <Input
                        id="collaborator_email"
                        type="email"
                        placeholder="spouse@example.com"
                        value={collaboratorEmail}
                        onChange={(e) => setCollaboratorEmail(e.target.value)}
                        data-testid="collaborator-email-input"
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        They'll be able to add and manage items in this wishlist.
                      </p>
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-collaborator-button">
                      Share Event
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              
              <InvitationBuilder eventId={eventId} event={event} onSuccess={() => { fetchEventData(); setHasInvitation(true); }} />
              {hasInvitation && (
                <>
                  <Button
                    onClick={() => window.open(`/i/${eventId}`, '_blank')}
                    variant="outline"
                    className="gap-2"
                    data-testid="view-invitation-button"
                  >
                    <Eye className="w-4 h-4" />
                    View Invitation
                  </Button>
                  <Button
                    onClick={handleShareInvitation}
                    className="gap-2 bg-blue-500 hover:bg-blue-600"
                    data-testid="share-invitation-button"
                  >
                    <Share2 className="w-4 h-4" />
                    Share Invitation
                  </Button>
                  <Button
                    onClick={fetchRSVPs}
                    variant="outline"
                    className="gap-2"
                    data-testid="view-rsvp-button"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    View RSVP List
                  </Button>
                </>
              )}
              <Button
                onClick={handleShareWishlist}
                className="gap-2 bg-green-500 hover:bg-green-600"
                data-testid="share-wishlist-button"
              >
                <Share2 className="w-4 h-4" />
                Share Wishlist
              </Button>
              <Button
                onClick={openAddressDialog}
                variant="outline"
                className="gap-2"
                data-testid="manage-address-button"
              >
                <MapPin className="w-4 h-4" />
                {event?.delivery_address ? 'Update Address' : 'Add Delivery Address'}
              </Button>
            </div>
          </div>
          
          {/* Address Info Display */}
          {event?.delivery_address && (
            <div className="mt-4 bg-blue-50 border-l-4 border-blue-500 p-4">
              <p className="text-sm text-gray-700">
                <strong>✅ Delivery address added:</strong> Gifters who select items can request this address.
              </p>
            </div>
          )}
        </div>

        {/* Wishlist Items */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800" data-testid="wishlist-heading">Wishlist Items</h2>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600" data-testid="add-item-button">
                  <Plus className="w-4 h-4" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md" data-testid="add-item-dialog">
                <DialogHeader>
                  <DialogTitle>Add Wishlist Item</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddItem} className="space-y-4">
                  {/* Step 1: Product URL */}
                  <div>
                    <Label htmlFor="product_url">
                      Product URL * 
                      <span className="text-xs text-gray-500 ml-2">(Paste link from Amazon, Myntra, etc.)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="product_url"
                        placeholder="https://amazon.in/..."
                        value={formData.product_url}
                        onChange={(e) => setFormData({ ...formData, product_url: e.target.value })}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text').trim();
                          setFormData({ ...formData, product_url: pastedText });
                        }}
                        data-testid="product-url-input"
                        required
                      />
                      <Button
                        type="button"
                        onClick={handleFetchProduct}
                        disabled={fetchingImage || !formData.product_url || formData.product_url.length < 10}
                        className="whitespace-nowrap bg-blue-500 hover:bg-blue-600"
                        data-testid="fetch-product-button"
                      >
                        {fetchingImage ? '⏳ Fetching...' : '🔍 Auto-Fill'}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Click "Auto-Fill" to automatically extract product details
                    </p>
                  </div>

                  {/* Step 2: Manual Fields */}
                  <div className="border-t pt-4">
                    <p className="text-sm text-gray-600 mb-3">
                      {formData.product_name || formData.price || formData.product_image_url 
                        ? '✓ Details fetched! Edit if needed:' 
                        : 'Fill these details manually:'}
                    </p>
                    
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="product_name">Product Name *</Label>
                        <Input
                          id="product_name"
                          placeholder="e.g., Smart Watch"
                          value={formData.product_name}
                          onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                          data-testid="product-name-input"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="price">Price (₹) *</Label>
                        <Input
                          id="price"
                          type="number"
                          step="0.01"
                          placeholder="2999"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          data-testid="product-price-input"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="product_image_url">
                          Product Image 
                          <span className="text-xs text-gray-500 ml-2">(Optional - placeholder used if empty)</span>
                        </Label>
                        <Input
                          id="product_image_url"
                          placeholder="https://... or upload below"
                          value={formData.product_image_url}
                          onChange={(e) => setFormData({ ...formData, product_image_url: e.target.value })}
                          data-testid="product-image-input"
                        />
                        
                        {/* Image Upload Option */}
                        <div className="mt-2">
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Or upload an image (max 2MB). A placeholder will be used if no image is provided.
                          </p>
                        </div>
                        
                        {formData.product_image_url && (
                          <img 
                            src={formData.product_image_url} 
                            alt="Preview" 
                            className="mt-2 w-full h-32 object-cover rounded border"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    data-testid="submit-item-button"
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? '⏳ Uploading Image...' : 'Add to Wishlist'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Price Filter */}
          {items.length > 0 && (
            <div className="mb-6 flex gap-2 flex-wrap" data-testid="price-filter">
              <Button
                onClick={() => setPriceFilter('all')}
                variant={priceFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                data-testid="filter-all"
              >
                All Items
              </Button>
              <Button
                onClick={() => setPriceFilter('under500')}
                variant={priceFilter === 'under500' ? 'default' : 'outline'}
                size="sm"
                data-testid="filter-under500"
              >
                Under ₹500
              </Button>
              <Button
                onClick={() => setPriceFilter('500-2000')}
                variant={priceFilter === '500-2000' ? 'default' : 'outline'}
                size="sm"
                data-testid="filter-500-2000"
              >
                ₹500 - ₹2000
              </Button>
              <Button
                onClick={() => setPriceFilter('2000-5000')}
                variant={priceFilter === '2000-5000' ? 'default' : 'outline'}
                size="sm"
                data-testid="filter-2000-5000"
              >
                ₹2000 - ₹5000
              </Button>
              <Button
                onClick={() => setPriceFilter('above5000')}
                variant={priceFilter === 'above5000' ? 'default' : 'outline'}
                size="sm"
                data-testid="filter-above5000"
              >
                Above ₹5000
              </Button>
            </div>
          )}

          {items.length === 0 ? (
            <div className="text-center py-12" data-testid="no-items-message">
              <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No items yet. Add your first wishlist item!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="items-grid">
              {items.filter(item => {
                if (priceFilter === 'all') return true;
                if (priceFilter === 'under500') return item.price < 500;
                if (priceFilter === '500-2000') return item.price >= 500 && item.price < 2000;
                if (priceFilter === '2000-5000') return item.price >= 2000 && item.price < 5000;
                if (priceFilter === 'above5000') return item.price >= 5000;
                return true;
              }).map((item) => (
                <div
                  key={item.id}
                  className={`glass-card p-4 product-card group relative ${item.product_url ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
                  data-testid={`item-card-${item.id}`}
                  onClick={() => {
                    if (item.product_url) {
                      window.open(item.product_url, '_blank');
                    }
                  }}
                >
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteItem(item.id);
                    }}
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    data-testid={`delete-item-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                  <img
                    src={item.product_image_url}
                    alt={item.product_name}
                    className="w-full h-48 object-cover rounded-lg mb-3"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
                    }}
                    data-testid={`item-image-${item.id}`}
                  />
                  <h3 className="font-semibold text-gray-800 mb-1" data-testid={`item-name-${item.id}`}>{item.product_name}</h3>
                  <p className="text-xl font-bold text-blue-600 mb-2" data-testid={`item-price-${item.id}`}>₹{item.price.toFixed(2)}</p>
                  {item.product_url && (
                    <p className="text-xs text-blue-500 mb-1">Click to view product</p>
                  )}
                  {item.selected_by && (
                    <p className="text-sm text-green-600 font-medium" data-testid={`item-selected-${item.id}`}>
                      {isCreatorOrCollaborator() && item.selected_by_name ? (
                        <>
                          ❤️ Selected by {item.selected_by_name}
                        </>
                      ) : (
                        <>
                          ❤️ Reserved by a loved one
                        </>
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Address Management Dialog */}
      <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delivery Address</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateAddress} className="space-y-4">
            <div>
              <Label htmlFor="delivery_address">Delivery Address *</Label>
              <textarea
                id="delivery_address"
                placeholder="Full address for gift delivery"
                value={addressData.delivery_address}
                onChange={(e) => setAddressData({ ...addressData, delivery_address: e.target.value })}
                className="w-full p-2 border rounded-md min-h-[100px]"
                required
                data-testid="address-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will only be visible to gifters who have selected an item.
              </p>
            </div>
            <div>
              <Label htmlFor="delivery_phone">Phone Number (Optional)</Label>
              <Input
                id="delivery_phone"
                type="tel"
                placeholder="+91 XXXXXXXXXX"
                value={addressData.delivery_phone}
                onChange={(e) => setAddressData({ ...addressData, delivery_phone: e.target.value })}
                data-testid="phone-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="save-address-button">
              Save Delivery Address
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* RSVP List Dialog */}
      <Dialog open={rsvpDialogOpen} onOpenChange={setRsvpDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>RSVP Guest List</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {rsvps.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No RSVPs yet</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-gray-600">
                    Total Guests: <strong>{rsvps.filter(r => r.attending).reduce((sum, r) => sum + (r.num_guests || 1), 0)}</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    Responses: <strong>{rsvps.length}</strong>
                  </p>
                </div>
                
                <div className="space-y-3">
                  {rsvps.map((rsvp, index) => (
                    <div 
                      key={rsvp.id || index}
                      className={`p-4 rounded-lg border-2 ${
                        rsvp.attending 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-800">{rsvp.guest_name}</h3>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              rsvp.attending 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {rsvp.attending ? '✓ Attending' : '✗ Not Attending'}
                            </span>
                          </div>
                          
                          {rsvp.guest_email && (
                            <p className="text-sm text-gray-600 mt-1">📧 {rsvp.guest_email}</p>
                          )}
                          
                          {rsvp.guest_phone && (
                            <p className="text-sm text-gray-600 mt-1">📱 {rsvp.guest_phone}</p>
                          )}
                          
                          {rsvp.attending && rsvp.num_guests > 1 && (
                            <p className="text-sm text-gray-600 mt-1">
                              👥 Bringing {rsvp.num_guests} guests
                            </p>
                          )}
                          
                          {rsvp.message && (
                            <p className="text-sm text-gray-700 mt-2 italic">
                              💬 "{rsvp.message}"
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetail;